// Mill barcode: 24 digits, tag(7) section(4) grade(2) heat(5) length(FFII) pcs(2).
// Post-cut QR: tag-section-grade-heat-<pcs, empty>-part#-length(decimal in)-qty
// (real tags deviate from the written spec: pcs slot empty, qty last, not FFII).

export const ENTRY_BARCODE_DIGITS = 24;

// B-TS-000123 -> 000123: prefix and line only in the QR
export const shownTagNo = (tagNo: string) =>
	/^[A-Za-z]/.test(tagNo) ? tagNo.slice(tagNo.lastIndexOf("-") + 1) : tagNo;

export interface ParsedSawEntryBarcode {
	tagNo: string;
	sectionName: string | null;
	gradeName: string | null;
	heatNo: string | null;
	millLengthIn: number;
	pieces: number | null;
	// Only a post-cut tag carries it.
	partNumber: string | null;
	raw: string;
}

// Strips non-digit noise (prefix chars, CR); null if not a 24-digit tag.
export const parseSawEntryBarcode = (
	input: string,
): ParsedSawEntryBarcode | null => {
	const raw = (input ?? "").replace(/\D/g, "");
	if (raw.length !== ENTRY_BARCODE_DIGITS) return null;

	const millFeet = Number(raw.slice(18, 20));
	const millInches = Number(raw.slice(20, 22));

	return {
		tagNo: raw.slice(0, 7),
		sectionName: raw.slice(7, 11),
		gradeName: raw.slice(11, 13),
		heatNo: raw.slice(13, 18),
		millLengthIn: millFeet * 12 + millInches,
		pieces: Number(raw.slice(22, 24)),
		partNumber: null,
		raw,
	};
};

// The middle tokens re-join as the part number (they may contain dashes).
// Null unless tag and a positive length parse; the part may be empty (mill tag).
export const parseSawEntryQr = (
	input: string,
): ParsedSawEntryBarcode | null => {
	const raw = (input ?? "").trim();
	if (!raw.includes("-")) return null;

	const all = raw.split("-").map((s) => s.trim());
	// Letter-led tag spans 3 tokens: line-code-number (B-TS-000123).
	const lettered = /^[A-Za-z]/.test(all[0]);
	const tagLen = lettered ? 3 : 1;
	const tagNo = all.slice(0, tagLen).join("-");
	const t = all.slice(tagLen - 1);
	if (t.length < 7) return null;

	const [, sectionName, gradeName, heatNo] = t;
	// Tags without the trailing qty end in the length instead.
	const hasQty = /^\d+$/.test(t[t.length - 1]);
	const lengthTok = hasQty ? t[t.length - 2] : t[t.length - 1];
	const qtyTok = hasQty ? t[t.length - 1] : "";
	const middle = t.slice(4, hasQty ? t.length - 2 : t.length - 1);
	if (middle.length === 0) return null;

	const hasPcsSlot =
		middle.length > 1 && (middle[0] === "" || /^\d{1,2}$/.test(middle[0]));
	const pcsSlot = hasPcsSlot ? middle[0] : "";
	const partNumber = (hasPcsSlot ? middle.slice(1) : middle).join("-");

	if (
		!(lettered ? /^[A-Za-z]+-[A-Za-z0-9]{1,4}-\d{6,}$/ : /^\d{7}$/).test(tagNo)
	)
		return null;
	const millLengthIn = Number(lengthTok);
	if (!Number.isFinite(millLengthIn) || millLengthIn <= 0) return null;

	const piecesTok = qtyTok || pcsSlot;
	const pieces = /^\d+$/.test(piecesTok) ? Number(piecesTok) : null;

	return {
		tagNo,
		sectionName: sectionName || null,
		gradeName: gradeName || null,
		heatNo: heatNo || null,
		millLengthIn,
		pieces,
		partNumber: partNumber || null,
		raw,
	};
};

// Only a QR carries separators.
export const isSawEntryQr = (input: string): boolean =>
	input?.includes("-") ?? false;

// Accepts either tag format.
export const parseSawEntryTag = (
	input: string,
): ParsedSawEntryBarcode | null =>
	isSawEntryQr(input) ? parseSawEntryQr(input) : parseSawEntryBarcode(input);

// 564 -> 47' 0"
export const formatMillLengthIn = (inches: number | null): string | null => {
	if (inches == null) return null;
	const feet = Math.floor(inches / 12);
	const rem = Math.round(inches - feet * 12);
	return `${feet}' ${rem}"`;
};

// QR lengths are decimal inches; 24-digit mill tags read as feet + inches.
export const formatEntryLength = (
	inches: number | null,
	isQr: boolean,
): string | null =>
	inches == null ? null : isQr ? `${inches}"` : formatMillLengthIn(inches);

if (import.meta.env.DEV) {
	const cases: [string, string][] = [
		["300001226804779345460624", "3000012|2680|47|79345|558|24|"],
		[
			"1234567-2695-41-12345--02002706-101.38-96",
			"1234567|2695|41|12345|101.38|96|02002706",
		],
		["3000001-2680-47-79345---558-24", "3000001|2680|47|79345|558|24|"],
		["B-TS-000123-2680-47-79345---558-24", "B-TS-000123|2680|47|79345|558|24|"],
	];
	for (const [input, want] of cases) {
		const p = parseSawEntryTag(input);
		const got =
			p &&
			Object.values({ ...p, raw: undefined })
				.slice(0, 7)
				.join("|");
		console.assert(got === want, "entryBarcode self-check failed", input, got);
	}
}
